import { Button } from "primereact/button";
import { Calendar } from 'primereact/calendar';
import React, { CSSProperties, createRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from '../../hooks';
import { ValidationInputEvent } from './validation-input';
import './validation-input.css';

export interface ValidationCalendarProps {
    id?: string;
    caption?: string;
    value?: Date | Date[];
    onChange?: (e: ValidationInputEvent) => void;
    onBlur?: (e: ValidationInputEvent) => void;

    minDate?: Date;
    maxDate?: Date;
    selectionMode?: "single" | "multiple" | "range";
    dateFormat?: string;
    showTime?: boolean;
    hourFormat?: "12" | "24";

    keyName?: string;
    required?: boolean;
    errorMessage?: string;
    readOnly?: boolean;

    disabled?: boolean;
    className?: string;
    editorClassName?: string;

    showButton?: boolean;
    buttonIcon?: string;
    buttonIconColor?: string;
    buttonToolTip?: string;
    placeholder?: string;
    buttonTitle?: string;
    style?: CSSProperties;
    containerStyle?: CSSProperties;
    onClick?: (e: any) => void;
    labelStyle?: React.CSSProperties;
    onEnter?: (e: any) => void;
    onValidationChanged?: (valid: boolean, id?: string) => void;
    onTranslate?: (value: string) => string;
    validationBounce?: number;
}

/**
 * Flexible input component that supports validation feedback, dropdown modes, and custom translations.
 * @param props - Configuration describing input type, validation requirements, and render options.
 * @param ref - Ref exposing a validate method for parent components.
 * @returns A validation-aware input or dropdown renderer with optional action button.
 */
export const ValidationCalendar = React.forwardRef((props: ValidationCalendarProps, ref) => {

    const translate = useTranslation();
    const [_value, setValue] = useState<Date | Date[] | undefined>(props.value)
    const [_autoCompleteValue, setAutoCompleteValue] = useState<any>();
    const [_className, setClassName] = useState<string>();
    const _editorHelpRef = createRef<HTMLElement>();

    const [_errorMessage, setErrorMessage] = useState<string>(props.errorMessage ?? '')

    const [_hasChanged, setHasChanged] = useState<boolean>(false);

    /**
     * Resolves translation text via external handler or default hook.
     * @param value - Translation key to resolve.
     * @returns Resolved localized string.
     */
    const internalTranslate = (value: string) => {
        if (props.onTranslate) {
            return props.onTranslate(value)
        }
        return translate(value);
    }



    useEffect(() => {
        setErrorMessage(props.errorMessage ?? '')
    }, [props.errorMessage])

    /**
     * Surfaces validation to parent refs based on the current input mode.
     */
    useImperativeHandle(ref, () => ({
        validate() {

            return validation();
        }
    }));

    useEffect(() => {
        if (_editorHelpRef.current) {
            if (_errorMessage) {
                setClassName('p-invalid');
                _editorHelpRef.current.style.display = 'block';
                _editorHelpRef.current.innerHTML = _errorMessage;
            } else {
                setClassName('');
                _editorHelpRef.current.style.display = 'none';
            }
        }



    }, [_errorMessage, _editorHelpRef])



    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    /**
     * Performs validation on text input values and emits state updates.
     * @returns True if the current value satisfies validation rules.
     */
    const validation = useCallback(() => {

        if (!_hasChanged) {
            return true;
        }

        const clearValidation = () => {
            setErrorMessage(props.errorMessage ?? '');
        }

        const showRequired = () => {
            setErrorMessage(internalTranslate("REQUIRED"));
        }

        let isValid = true;

        if (_value === undefined && props.required) {
            showRequired();
            isValid = false;
        }
        else {
            if (Array.isArray(_value)) {
                if (_value?.length > 0) {
                    clearValidation()
                }
                else if (props.required) {
                    showRequired();
                    isValid = false;
                }
            }
            else if (_value instanceof Date) {
                clearValidation();
            }
            else {
                showRequired();
                isValid = false;
            }
        }
        if (props.onValidationChanged) {
            props.onValidationChanged(isValid, props.id);
        }
        return isValid;

    }, [_value, props.errorMessage, _hasChanged]);



    /**
     * Displays the required indicator when the caption is present.
     */
    const renderRequired = () => {
        const show = props.required && props.caption ? true : false
        return show ? <span className="fas fa-star required-star"  ></span> : ''
    }

    /**
     * Renders the text input variant including optional button and password toggle.
     * @returns JSX structure for the text input flavor.
     */
    const renderInputBox = () => {
        const handleDateChanged = (e: any) => {

            setValue(e.value);
            setHasChanged(true);
            if (props.onChange) {
                props.onChange({
                    value: e.value,
                    originalEvent: e
                })
            }



        }

        const handleOnBlur = (e: any) => {
            // validation();
            if (props.onBlur)
                props.onBlur({
                    value: e.value,
                    originalEvent: e
                })
        }


        return (
            <div className={props.caption ? 'field p-fluid' : ''} key={props.keyName} style={{ width: '100%', ...props.style }}>
                {renderRequired()}
                {props.caption ? <><label style={props.labelStyle} className="item-authors">{internalTranslate(props.caption)}</label></> : ''}
                {props.showButton ?
                    <Button onClick={props.onClick} title={props.buttonTitle}
                        icon={props.buttonIcon} style={{ float: "right", height: 20, color: props.buttonIconColor ?? 'black' }} className="p-button-secondary p-button-text" aria-label={props.buttonToolTip} />
                    : <></>
                }
                <Calendar id={props.id} selectionMode={props.selectionMode ?? "single"} value={_value} disabled={props.disabled} readOnlyInput={props.readOnly}
                    onBlur={handleOnBlur} placeholder={props.placeholder} className={`${_className ?? ''} ${props.editorClassName ?? ''}`}
                    onChange={handleDateChanged} dateFormat={props.dateFormat ?? "yy-mm-dd"} showTime={props.showTime ?? false}
                    hourFormat={props.hourFormat ?? "24"} minDate={props.minDate} maxDate={props.maxDate} />



                <small ref={_editorHelpRef} id={`${props.id}-help`} style={{ display: 'none', marginLeft: 2 }} className="p-error p-d-block"></small>
            </div>
        )
    }

    return (
        <div className={props.className ?? ""} style={{ width: '100%', ...props.containerStyle }}>
            {
                renderInputBox()
            }
        </div>
    )
});

